import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { OperacionesClienteFinal, ResultadoIdentificacion, TokenCliente } from '../api/ops/clienteFinal';
import type { EntradaDelPedido, EventoPublico, OperacionesCompraEntradas, PedidoEnLineaCreado, PresupuestoEntradas } from '../api/ops/compraEntradas';
import { claveIdempotencia } from '../api/comun';
import { partesDelQr } from '../api/ops/pasesCliente';
import { ErrorApi } from '../api/problema';
import { Campo } from '../componentes/Campo';
import { Chip } from '../componentes/Chip';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { minutosYSegundos, useCuentaAtras } from '../logica/cuentaAtras';
import { dinero } from '../logica/dinero';
import { Contador } from '../componentes/Contador';
import { diaLargo, horaEn, hoyEn } from '../pantallas/reservas/fechas';
import { CADUCA_PRONTO_SEGUNDOS, CLAVE_PEDIDO_ENTRADAS, CONDICIONES, GUARDADAS_SEGUNDOS, avisoDeLaFicha, chipDeLaFicha, codigoRrpp, contactoIdentificado, cuandoEvento, cuandoLargo, enCarrito, lineasDeLaFicha, textoCambioDeFecha, estadoVenta, faltanNombres, lineaDePase, lineasDeCompra, maximoDe, leerPendiente, porDonde, resultadoDeEstado, sePuedeComprar, textoDelFallo, textoEnvio, textoHecha, textoSinVenta, vueltaDe, type Carrito, type ContactoCompra, type Nombres, type PedidoPendiente, type ResultadoVuelta } from './compra';
import { Identificarse } from './Identificarse';
import { BotonesCartera, NotaPase } from './PaseComun';
import { useCarteras, type Nota } from './carteras';
import { AvisoPc, Baldosa, BotonPc, EnlaceLegal, LegalCaptcha } from './Pagina';
import { cierreDeEntrada, esBaja, leerEntrada, type Cierre } from './entradaCerrada';
import { marcaDelWidget, tintaSobre, type Tema } from './marca';
import './reserva.css';
import './compra.css';

/**
 * 10.13 Compra pública de entradas (tarea 54; G901, ADR-0227): una página del cliente en `PANEL_URL`, como la reserva
 * pública (G879), con la marca del negocio y sin el negro ni el rojo de Reservaria (G614, G618).
 *
 * - Web: `/comprar-entradas/{slug}` → `getPublicEntry` (G885: aunque el negocio no tenga web publicada). Cerrada (G932):
 *   en pausa, por temporada (con `reopens_on`) o retirada, una tarjeta que no vende; con `410`, «Aquí ya no se venden
 *   entradas» sin marca ni negocio (`entradaCerrada.ts`).
 * - Resultado: cada pase con su página (6.6) y las carteras (G53; botones neutros hasta las insignias oficiales, Q4).
 * - Widget: `/comprar-entradas?clave=pk_…&marco=widget[&tema=][&acento=]` → `getPublicWidget` (marco de
 *   `entradas.cartelera`, `entradas.proximo_evento` y `entradas.comprar`), con «Privacidad de [negocio]» en el pie (G869).
 * - `&evento=evn_…` (o el `event_id` del widget) abre ese evento; si no, la cartelera (`listPublicEvents`). `?rrpp=` se
 *   pasa como `promoter_ref`, sin interpretarlo.
 *
 * Pasos: cartelera → ficha (descripción, «De… a…», «Puertas a las…», estado y tipos; tarea 64) → elegir (tipos con su contador, nombres en las nominativas, G3) → identificarse (2.9) → revisar
 * (`quoteTicketOrder`: entradas, gastos de gestión y total de la API) → comprar (`createOnlineTicketOrder` con antibot,
 * `expected_total` e `Idempotency-Key`; precio cambiado `409 conflict`, sin plazas `409`) → pagar (30 min, G39) o hecha.
 *
 * - Sin elegir por dónde llega el pase (G787): va por el contacto con el que se identificó (el teléfono, por WhatsApp; el
 *   email, por email). Antes de identificarse se explica; en Revisar y en Hecha se dice a dónde. Con un token de antes,
 *   el de la cuenta (`getMyCustomerAccount`: el teléfono y, sin él, el email).
 * - Pagar: la cuenta atrás de `next_action.expires_at` («Caduca pronto» a 5 minutos); a cero, «Se han liberado tus
 *   entradas». Hecha: un pase por entrada con su página (6.6, `qr_url` de `getMyPass`).
 * - Vuelta del pago (`return_url` = esta página con `?vuelta=1`; el pedido guardado en la pestaña): `getOnlineTicketOrderStatus`
 *   con el `order_token`. Confirmado: «hecha» con las entradas de `getMyTicketOrder` y sus pases; pendiente: «Esperando a tu
 *   banco» (se vuelve a mirar cada pocos segundos y con «Comprobar ahora»); caducado: «Se han liberado tus entradas»;
 *   devuelto: «Te devolvemos el dinero»; cancelado: «No se ha podido cobrar».
 */
export interface PropsComprarEntradas {
    ops: OperacionesClienteFinal;
    entradas: OperacionesCompraEntradas;
    /** `slug` del negocio; sin él, el widget con `clave`. */
    slug: string | null;
    busqueda: URLSearchParams;
    captcha: (accion: string) => Promise<string | null>;
    onToken: (token: TokenCliente) => void;
    /** Ya tiene token de cliente: se identifica en el negocio sin pedir otro código. */
    conToken?: boolean;
    navegar: (ruta: string) => void;
    /** Abre la página de pago (por defecto, el navegador). */
    abrir?: (url: string) => void;
    ahora?: () => number;
    /** Dónde se guarda el pedido mientras se paga (por defecto, sessionStorage). */
    almacen?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
    /** Cada cuánto se vuelve a mirar un pago pendiente a la vuelta (ms). */
    espera?: number;
}

interface Hecha {
    referencia: string;
    total: PresupuestoEntradas['total'] | null;
    tickets: readonly EntradaDelPedido[];
    n: number;
    perfil: string | null;
}

function almacenDeLaPestana(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> {
    try {
        return window.sessionStorage;
    } catch {
        const m = new Map<string, string>();
        return { getItem: (k) => m.get(k) ?? null, setItem: (k, v) => { m.set(k, v); }, removeItem: (k) => { m.delete(k); } };
    }
}

interface Negocio {
    id: string;
    nombre: string;
    clave: string;
    privacidad: string;
    /** «Volver a la web» (G988): `PublicEntry.site_url`, el dominio propio activo o la dirección pública del negocio; sin ella, no sale. */
    web: string | null;
    zona: string;
    marca: string;
    tema: Tema;
    widget: boolean;
    /** Cerrada (`getPublicEntry`, G885/G932; o el widget no activo, como `pausado`): la página no vende. */
    cierre: Cierre | null;
    eventoDelWidget: string | null;
}

type Paso = 'cartelera' | 'ficha' | 'elegir' | 'identificarse' | 'revisar' | 'pagar' | 'hecha';

const EVENTO = /^evn_[0-9A-HJKMNP-TV-Z]{26}$/;
const SIN_CLAVE = 'sin-clave';

/** La entrada cerrada (G885, G932): baldosa, qué pasa y «Mis entradas» (con `410`, sin botón); no vende. */
function TarjetaCerrada({ icono, titulo, texto, onMisEntradas }: { icono: NombreIcono; titulo: string; texto: string; onMisEntradas?: () => void }) {
    return (
        <section className="rv-pc-card">
            <div className="rv-pc-baldosa-fila"><Baldosa icono={icono} fondo="var(--rv-paper)" color="var(--rv-ink)" /></div>
            <div className="rv-pc-card__cab"><h1>{titulo}</h1><span>{texto}</span></div>
            {onMisEntradas !== undefined && <BotonPc texto="Mis entradas" icono="ticket" tipo="sec" onClick={onMisEntradas} />}
        </section>
    );
}

/** `site_url` solo si es una dirección web (`http`/`https`); cualquier otra cosa no se enlaza. */
function enlaceDeLaWeb(url: string | null | undefined): string | null {
    return url != null && /^https?:\/\//i.test(url) ? url : null;
}

function Marco({ negocio, linea, children }: { negocio: Negocio | null; linea: string; children: ReactNode }) {
    const marca = negocio?.marca ?? '#111111';
    const estilo = { '--rv-red': marca, '--rv-red-hover': marca, '--rv-rp-tinta': tintaSobre(marca) } as CSSProperties;
    return (
        <div className={`rv-pc rv-rp rv-ce rv-rp--${negocio?.tema ?? 'claro'}${negocio?.widget === true ? ' rv-rp--widget' : ''}`} style={estilo} data-pagina-cliente="">
            <div className="rv-pc__col">
                {negocio !== null && !negocio.widget && (
                    <header className="rv-pc__cab rv-rp__cab">
                        <span aria-hidden="true" className="rv-pc__avatar rv-rp__avatar">{negocio.nombre.slice(0, 1).toUpperCase()}</span>
                        <span className="rv-pc__marca"><strong>{negocio.nombre}</strong><span>{linea}</span></span>
                    </header>
                )}
                <main className="rv-pc__main">{children}</main>
                {negocio !== null && (negocio.privacidad !== '' || (!negocio.widget && negocio.web !== null)) && (
                    <footer className="rv-rp__pie">
                        {/* G988 (10.13): delante, «Volver a la web» si hay web publicada; en el widget no sale (ya está en la web). */}
                        {!negocio.widget && negocio.web !== null && (
                            <>
                                <a className="rv-ce-volver-web" href={negocio.web}><Icono nombre="arrow-left" />Volver a la web</a>
                                {negocio.privacidad !== '' && <span aria-hidden="true">·</span>}
                            </>
                        )}
                        {negocio.privacidad !== '' && <EnlaceLegal url={negocio.privacidad}>{`Privacidad de ${negocio.nombre}`}</EnlaceLegal>}
                        {!negocio.widget && <><span aria-hidden="true">·</span><span>Ofrecido por Reservaria</span></>}
                    </footer>
                )}
            </div>
        </div>
    );
}

export function ComprarEntradas({ ops, entradas, slug, busqueda, captcha, onToken, conToken = false, navegar, abrir = (url) => { window.location.assign(url); }, ahora = Date.now, almacen: dado, espera = 4000 }: PropsComprarEntradas) {
    const [almacen] = useState(() => dado ?? almacenDeLaPestana());
    const [vuelta, setVuelta] = useState<{ pedido: PedidoPendiente; estado: ResultadoVuelta | 'comprobando'; motivo?: string | null } | null>(() => {
        const p = busqueda.get('vuelta') === '1' ? leerPendiente(almacen) : null;
        return p === null ? null : { pedido: p, estado: 'comprobando' };
    });
    const [hecha, setHecha] = useState<Hecha | null>(null);
    const [negocio, setNegocio] = useState<Negocio | null>(null);
    const [error, setError] = useState<'no-existe' | 'baja' | 'error' | null>(null);
    const [intento, setIntento] = useState(0);
    const [cartelera, setCartelera] = useState<readonly EventoPublico[] | null>(null);
    const [elegido, setElegido] = useState<string | null>(() => vuelta?.pedido.evento ?? (EVENTO.test(busqueda.get('evento') ?? '') ? busqueda.get('evento') : null));
    // `datos`: el evento; null si ya no está a la venta (404); 'cancelado' con `410 event_cancelled` (G1295).
    const [evento, setEvento] = useState<{ id: string; datos: EventoPublico | null | 'cancelado' } | null>(null);
    const [paso, setPaso] = useState<Paso>('cartelera');
    const [carrito, setCarrito] = useState<Carrito>({});
    const [nombres, setNombres] = useState<Nombres>({});
    const [cliente, setCliente] = useState<ResultadoIdentificacion | null>(null);
    const [presupuesto, setPresupuesto] = useState<PresupuestoEntradas | null>(null);
    const [aviso, setAviso] = useState<{ texto: string; tipo: 'error' | 'espera' | 'info' } | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [creado, setCreado] = useState<PedidoEnLineaCreado | null>(null);
    const [contacto, setContacto] = useState<ContactoCompra | null>(() => vuelta?.pedido.contacto ?? null);
    const [nombreCliente, setNombreCliente] = useState<string | null>(null);
    const [pases, setPases] = useState<Readonly<Record<string, string>>>({});
    // G53: cada pase del resultado con las insignias «Añadir a Cartera de Apple» y «Añadir a Google Wallet» (6.8, C.23).
    const [notaCartera, setNotaCartera] = useState<Nota | null>(null);
    const { ocupada, anadir } = useCarteras(ops, setNotaCartera);
    const queda = useCuentaAtras(paso === 'pagar' ? (creado?.next_action?.expires_at ?? null) : null, ahora);
    const idem = useRef(claveIdempotencia());
    const claveWidget = busqueda.get('clave');

    // El negocio: por el slug (entrada pública) o por la clave del widget.
    useEffect(() => {
        let vivo = true;
        const cargar = async (): Promise<Negocio> => {
            if (slug !== null) {
                const w = await leerEntrada(() => entradas.getPublicEntry(slug));
                return { id: w.business.business_id, nombre: w.business.name, clave: w.api_key, privacidad: w.privacy_url, web: enlaceDeLaWeb(w.site_url), zona: w.business.timezone, marca: w.brand?.primary_color ?? '#111111', tema: 'claro', widget: false, cierre: cierreDeEntrada(w), eventoDelWidget: null };
            }
            if (claveWidget === null || claveWidget === '') {
                throw new Error(SIN_CLAVE);
            }
            const w = await entradas.getPublicWidget(claveWidget);
            const m = marcaDelWidget(w, busqueda);
            const opciones = (w.options ?? {}) as Record<string, unknown>;
            const delWidget = typeof opciones.event_id === 'string' && EVENTO.test(opciones.event_id) ? opciones.event_id : null;
            return { id: w.business_id, nombre: w.business_name ?? '', clave: claveWidget, privacidad: w.privacy_url ?? '', web: null, zona: Intl.DateTimeFormat().resolvedOptions().timeZone, marca: m.acento, tema: m.tema, widget: true, cierre: w.status !== 'active' ? { motivo: 'pausado', vuelve: null } : null, eventoDelWidget: delWidget };
        };
        cargar()
            .then((n) => {
                if (!vivo) return;
                setNegocio(n);
                setError(null);
                setElegido((x) => x ?? n.eventoDelWidget);
            })
            .catch((x: unknown) => { if (vivo) { setError(esBaja(x) ? 'baja' : (x instanceof ErrorApi && (x.estado === 404 || x.estado === 410 || x.estado === 401)) || (x instanceof Error && x.message === SIN_CLAVE) ? 'no-existe' : 'error'); } });
        return () => { vivo = false; };
        // `busqueda` solo se lee al cargar.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entradas, slug, claveWidget, intento]);

    // La cartelera (sin evento elegido) o el evento.
    useEffect(() => {
        if (negocio === null || negocio.cierre !== null) return;
        let vivo = true;
        if (elegido === null) {
            entradas.listPublicEvents(negocio.id, negocio.clave)
                .then((l) => { if (vivo) { setCartelera(l.data); setPaso('cartelera'); } })
                .catch(() => { if (vivo) { setError('error'); } });
        } else {
            entradas.getPublicEvent(negocio.id, negocio.clave, elegido)
                .then((d) => { if (vivo) { setEvento({ id: elegido, datos: d }); setPaso((p) => (p === 'cartelera' ? 'ficha' : p)); } })
                .catch((x: unknown) => {
                    if (!vivo) return;
                    if (x instanceof ErrorApi && x.estado === 410 && x.codigo === 'event_cancelled') { setEvento({ id: elegido, datos: 'cancelado' }); } else if (x instanceof ErrorApi && x.estado === 404) { setEvento({ id: elegido, datos: null }); } else { setError('error'); }
                });
        }
        return () => { vivo = false; };
    }, [entradas, negocio, elegido, intento]);

    // Ya identificado: su nombre («A nombre de») y, si se identificó antes (con un token), el contacto con el que se
    // identificó, que dice la API (`MyProfile.identified_with`, G934).
    useEffect(() => {
        if (cliente === null || nombreCliente !== null) return;
        let vivo = true;
        ops.getMyCustomerAccount()
            .then(({ datos }) => { if (vivo) setNombreCliente(datos.display_name); })
            .catch(() => undefined);
        ops.listMyBusinessProfiles({ limit: 100 })
            .then((l) => {
                if (!vivo) return;
                const perfil = l.data.find((x) => x.customer_id === cliente.customer_id) ?? l.data[0];
                setContacto((c) => c ?? contactoIdentificado(perfil?.identified_with));
            })
            .catch(() => undefined);
        return () => { vivo = false; };
    }, [ops, cliente, nombreCliente]);

    // Hecha: la página de cada pase (6.6), con el `qr_url` de «Mis pases».
    useEffect(() => {
        const perfil = hecha?.perfil ?? null;
        if (hecha === null || perfil === null) return;
        let vivo = true;
        const ids = hecha.tickets.map((t) => t.pass_id).filter((x): x is string => typeof x === 'string');
        Promise.all(ids.map((id) => ops.getMyPass(perfil, id).then((x) => [id, x.qr_url] as const).catch(() => null)))
            .then((l) => { if (vivo) setPases(Object.fromEntries(l.filter((x) => x !== null))); })
            .catch(() => undefined);
        return () => { vivo = false; };
    }, [ops, hecha]);

    // La vuelta del pago: el estado del pedido con su `order_token`; confirmado, sus entradas (y pases) con el token de cliente.
    const comprobando = vuelta?.estado === 'comprobando';
    useEffect(() => {
        if (negocio === null || vuelta === null || !comprobando) return;
        let vivo = true;
        const p = vuelta.pedido;
        entradas.getOnlineTicketOrderStatus(negocio.id, negocio.clave, p.pedido, p.token)
            .then(async (st) => {
                const r = resultadoDeEstado(st.status, st.failure_reason ?? null);
                if (r !== 'procesando') almacen.removeItem(CLAVE_PEDIDO_ENTRADAS);
                if (r !== 'hecha') {
                    if (vivo) setVuelta({ pedido: p, estado: r, motivo: st.failure_reason ?? null });
                    return;
                }
                const pedido = p.perfil === null ? null : await entradas.getMyTicketOrder(p.perfil, p.pedido).catch(() => null);
                if (!vivo) return;
                const tickets = pedido?.tickets.filter((t) => t.status === 'valid') ?? [];
                setHecha({ referencia: st.reference, total: pedido?.total ?? p.total, tickets, n: pedido === null ? (st.tickets_count ?? 0) : tickets.length, perfil: p.perfil });
                setVuelta(null);
            })
            .catch(() => { if (vivo) setError('error'); });
        return () => { vivo = false; };
    }, [entradas, negocio, vuelta, comprobando, almacen, intento]);

    // Pendiente de pago a la vuelta: se vuelve a mirar sola cada pocos segundos.
    useEffect(() => {
        if (vuelta?.estado !== 'procesando') return;
        const t = setTimeout(() => { setVuelta((v) => (v === null ? v : { ...v, estado: 'comprobando' })); }, espera);
        return () => { clearTimeout(t); };
    }, [vuelta, espera]);

    const linea = 'Comprar entradas';
    if (error === 'baja') {
        // G932: con `410` no llega la marca: colores de Reservaria, sin cabecera ni nombre de ningún negocio (D-052).
        return (
            <Marco negocio={null} linea="">
                <TarjetaCerrada icono="calendar-x" titulo="Aquí ya no se venden entradas" texto="Este negocio ya no vende entradas por internet con Reservaria." />
            </Marco>
        );
    }
    if (error !== null) {
        return (
            <Marco negocio={negocio} linea={linea}>
                <section className="rv-pc-card">
                    <div className="rv-pc-card__cab"><h1>{error === 'no-existe' ? 'No encontramos esta página' : 'No hemos podido cargar las entradas'}</h1><span>{error === 'no-existe' ? 'Puede que el enlace ya no valga.' : 'Comprueba la conexión y prueba de nuevo.'}</span></div>
                    {error === 'error' && <BotonPc texto="Reintentar" icono="refresh-cw" onClick={() => { setError(null); setIntento((i) => i + 1); }} />}
                </section>
            </Marco>
        );
    }
    if (negocio === null) {
        return <Marco negocio={null} linea=""><section className="rv-pc-card" aria-busy="true" aria-label="Cargando"><div className="rv-pc-esqueleto" style={{ height: 56 }} /><div className="rv-pc-esqueleto" style={{ height: 160 }} /></section></Marco>;
    }
    if (negocio.cierre !== null) {
        const { cierre } = negocio;
        const misEntradas = () => { navegar('/mis-entradas'); };
        return (
            <Marco negocio={negocio} linea={linea}>
                {cierre.motivo === 'temporada' ? (
                    <TarjetaCerrada icono="calendar-clock" titulo={`${negocio.nombre} está cerrado por temporada`} texto={`Ahora no vende entradas por internet.${cierre.vuelve === null ? '' : ` Vuelve a abrir el ${diaLargo(cierre.vuelve)}.`} Si ya tienes entradas, las tienes en «Mis entradas».`} onMisEntradas={misEntradas} />
                ) : cierre.motivo === 'retirada' ? (
                    <TarjetaCerrada icono="eye-off" titulo="Esta página no está disponible" texto="Ahora no se pueden comprar entradas aquí. Si ya tienes entradas, siguen valiendo: las tienes en «Mis entradas»." onMisEntradas={misEntradas} />
                ) : (
                    <TarjetaCerrada icono="pause" titulo="Ahora no se pueden comprar entradas" texto={`${negocio.nombre} no vende por internet en este momento. Si ya tienes entradas, siguen valiendo: las tienes en «Mis entradas».`} onMisEntradas={misEntradas} />
                )}
            </Marco>
        );
    }

    const hoy = hoyEn(negocio.zona, new Date(ahora()));

    // ---- Cartelera ----
    if (elegido === null) {
        if (cartelera === null) {
            return <Marco negocio={negocio} linea={linea}><section className="rv-pc-card" aria-busy="true" aria-label="Cargando"><div className="rv-pc-esqueleto" style={{ height: 160 }} /></section></Marco>;
        }
        return (
            <Marco negocio={negocio} linea={linea}>
                <div className="rv-ce-titulo"><h1>Cartelera</h1><span>{`Compra tus entradas para ${negocio.nombre} y te llegan al momento.`}</span></div>
                {cartelera.length === 0 ? (
                    <section className="rv-pc-card"><div className="rv-pc-card__cab"><h2>Ahora no hay eventos a la venta</h2><span>Cuando publiquen el próximo, lo verás aquí.</span></div></section>
                ) : (
                    <ul className="rv-ce-cartelera">
                        {cartelera.map((x) => {
                            const estado = estadoVenta(x);
                            return (
                                <li key={x.id}>
                                    <button type="button" className="rv-ce-evento" onClick={() => { setCarrito({}); setNombres({}); setElegido(x.id); }} aria-label={`${x.name}, ${cuandoEvento(x, hoy)}`}>
                                        <span className="rv-ce-evento__cartel" style={x.image_url == null ? undefined : { backgroundImage: `url(${x.image_url})` }}>{x.image_url == null && <strong>{x.name}</strong>}</span>
                                        <span className={`rv-ce-evento__cuando${x.local_start_date === hoy ? ' rv-ce-evento__cuando--hoy' : ''}`}>{cuandoEvento(x, hoy)}</span>
                                        <strong className="rv-ce-evento__nombre">{x.name}</strong>
                                        {x.price_from != null && <small>{`Desde ${dinero(x.price_from)}`}</small>}
                                        {estado === 'agotado' && <span className="rv-ce-chip rv-ce-chip--agotado">Agotado</span>}
                                        {estado !== 'agotado' && x.rescheduled_from != null && <span className="rv-ce-chip">Fecha cambiada</span>}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Marco>
        );
    }

    // ---- Un evento ----
    const e = evento?.id === elegido ? evento.datos : undefined;
    if (e === undefined) {
        return <Marco negocio={negocio} linea={linea}><section className="rv-pc-card" aria-busy="true" aria-label="Cargando"><div className="rv-pc-esqueleto" style={{ height: 160 }} /></section></Marco>;
    }
    if (e === 'cancelado') {
        // G1295 (C.85): «Este evento se ha cancelado» con «Mis entradas»; la respuesta `410` no trae el evento.
        return (
            <Marco negocio={negocio} linea={linea}>
                <section className="rv-pc-card" role="status">
                    <div className="rv-pc-baldosa-fila"><Baldosa icono="calendar-x" fondo="var(--rv-red-soft)" color="var(--rv-red)" /><span className="rv-pc-card__cab"><h1>Este evento se ha cancelado</h1><span>Si compraste entradas, te devolvemos todo el dinero, gastos de gestión incluidos, y ya te lo hemos avisado. Puedes verlo en «Mis entradas».</span></span></div>
                    <BotonPc texto="Mis entradas" icono="ticket" onClick={() => { navegar('/mis-entradas'); }} />
                    {negocio.eventoDelWidget === null && <BotonPc texto="Ver la cartelera" icono="calendar" tipo="sec" onClick={() => { setElegido(null); }} />}
                </section>
            </Marco>
        );
    }
    if (e === null) {
        return (
            <Marco negocio={negocio} linea={linea}>
                <section className="rv-pc-card"><div className="rv-pc-card__cab"><h1>Este evento ya no está a la venta</h1><span>Puede que se haya cancelado o que ya haya pasado.</span></div>
                    {negocio.eventoDelWidget === null && <BotonPc texto="Ver la cartelera" icono="arrow-left" tipo="sec" onClick={() => { setElegido(null); }} />}
                </section>
            </Marco>
        );
    }

    const nominativo = e.nominative;
    const maxPorPedido = e.max_tickets_per_order ?? 10;
    const cuantas = enCarrito(carrito);
    const lineas = lineasDeCompra(carrito, nominativo, nombres);
    const cabeceraEvento = (
        <section className="rv-pc-card rv-ce-cabecera">
            <Icono nombre="party-popper" />
            <span><strong>{e.name}</strong><small>{`${cuandoEvento(e, hoy)}${e.door_opens_at == null ? '' : ` · puertas ${horaEn(e.door_opens_at, e.timezone)}`}`}</small></span>
            <button type="button" className="rv-rp-cambiar rv-ce-cabecera__ver" onClick={() => { setPaso('ficha'); }}><Icono nombre="info" /><span>Ver el evento</span></button>
        </section>
    );

    const presupuestar = () => {
        setOcupado(true);
        setAviso(null);
        entradas.quoteTicketOrderPublic(negocio.id, negocio.clave, { event_id: e.id, lines: lineas })
            .then((p) => { setPresupuesto(p); setPaso('revisar'); idem.current = claveIdempotencia(); })
            .catch((x: unknown) => { setAviso({ tipo: 'error', texto: x instanceof ErrorApi && x.estado === 409 ? 'Ya no quedan tantas entradas. Revisa lo elegido.' : 'No hemos podido calcular el precio. Prueba de nuevo.' }); })
            .finally(() => { setOcupado(false); });
    };

    const seguir = () => {
        if (cliente !== null) {
            presupuestar();
            return;
        }
        if (!conToken) {
            setPaso('identificarse');
            return;
        }
        setOcupado(true);
        ops.identifyCustomerInBusiness(negocio.id, negocio.clave, { source: negocio.widget ? 'widget' : 'web' })
            .then((r) => { setCliente(r); presupuestar(); })
            .catch(() => { setOcupado(false); setPaso('identificarse'); });
    };

    const comprar = async () => {
        if (presupuesto === null) return;
        setOcupado(true);
        setAviso(null);
        const token = await captcha('comprar_entradas');
        const rrpp = codigoRrpp(busqueda);
        const vueltaUrl = typeof window === 'undefined' ? null : vueltaDe(window.location.href);
        entradas.createOnlineTicketOrder(negocio.id, negocio.clave, { event_id: e.id, lines: lineas, expected_total: presupuesto.total, ...(rrpp === null ? {} : { promoter_ref: rrpp }), ...(vueltaUrl === null ? {} : { return_url: vueltaUrl }) }, token, idem.current)
            .then((r) => {
                setCreado(r);
                if (r.next_action?.url == null) {
                    const validas = r.order.tickets.filter((t) => t.status === 'valid');
                    setHecha({ referencia: r.order.reference, total: r.order.total, tickets: validas, n: validas.length, perfil: cliente?.customer_id ?? null });
                    setPaso('hecha');
                    return;
                }
                if (r.order_token !== undefined) {
                    const pendiente: PedidoPendiente = { pedido: r.order.id, token: r.order_token, perfil: cliente?.customer_id ?? null, evento: e.id, contacto, total: presupuesto.total, resumen: presupuesto.lines.map((l) => `${String(l.quantity)} × ${l.name ?? ''}`).join(' · ') };
                    almacen.setItem(CLAVE_PEDIDO_ENTRADAS, JSON.stringify(pendiente));
                }
                setPaso('pagar');
            })
            .catch((x: unknown) => {
                if (x instanceof ErrorApi && x.estado === 409 && x.codigo === 'conflict') {
                    // El precio ha cambiado: el nuevo presupuesto, sin cobrar nada.
                    entradas.quoteTicketOrderPublic(negocio.id, negocio.clave, { event_id: e.id, lines: lineas }).then(setPresupuesto).catch(() => undefined);
                    idem.current = claveIdempotencia();
                    setAviso({ tipo: 'espera', texto: 'El precio ha cambiado mientras comprabas. Revisa el nuevo antes de seguir.' });
                } else if (x instanceof ErrorApi && x.estado === 409) {
                    setPaso('elegir');
                    setIntento((i) => i + 1);
                    setAviso({ tipo: 'error', texto: 'Alguien se ha adelantado y ya no quedan tantas entradas. Elige otra vez: no te hemos cobrado nada.' });
                } else if (x instanceof ErrorApi && x.codigo === 'captcha_required') {
                    setAviso({ tipo: 'error', texto: 'No hemos podido comprobar que no eres un robot. Prueba otra vez: no se ha borrado nada.' });
                } else {
                    setAviso({ tipo: 'error', texto: 'No hemos podido hacer la compra. Prueba de nuevo: no se ha borrado nada.' });
                }
            })
            .finally(() => { setOcupado(false); });
    };

    const nombreDeTipo = (id: string) => e.ticket_types.find((t) => t.id === id)?.name ?? '';
    const puertas = e.door_opens_at == null ? null : horaEn(e.door_opens_at, e.timezone);
    const volverAComprar = () => {
        almacen.removeItem(CLAVE_PEDIDO_ENTRADAS);
        setVuelta(null);
        setCreado(null);
        setAviso(null);
        idem.current = claveIdempotencia();
        setPaso('elegir');
        setIntento((i) => i + 1);
    };

    const resultado = (icono: NombreIcono, fondo: [string, string], chip: [string, 'problem' | 'confirmed' | 'pending'], titulo: string, texto: string, resto: ReactNode, girando = false) => (
        <Marco negocio={negocio} linea={linea}>
            <section className="rv-pc-card" aria-labelledby="rv-ce-resultado">
                <div className="rv-pc-baldosa-fila">
                    <span aria-hidden="true" className="rv-pc-baldosa" style={{ background: fondo[0], color: fondo[1] }}><Icono nombre={icono} className={girando ? 'rv-gira' : undefined} /></span>
                    <Chip estado={chip[1]}>{chip[0]}</Chip>
                </div>
                <div className="rv-pc-card__cab"><h1 id="rv-ce-resultado">{titulo}</h1><span>{texto}</span></div>
                {resto}
            </section>
        </Marco>
    );
    const liberadas = () => resultado('hourglass', ['var(--rv-paper)', 'var(--rv-ink)'], ['Caducado', 'problem'], 'Se han liberado tus entradas', 'Han pasado los 30 minutos sin pagar. No te hemos cobrado nada. Si quedan entradas, puedes volver a comprarlas.', <BotonPc texto="Volver a comprar" icono="ticket" onClick={volverAComprar} />);

    if (vuelta !== null) {
        const p = vuelta.pedido;
        switch (vuelta.estado) {
            case 'liberada':
                return liberadas();
            case 'rechazado':
                return resultado('x', ['var(--rv-red-soft)', 'var(--rv-red)'], ['Rechazado', 'problem'], 'No se ha podido cobrar', textoDelFallo(vuelta.motivo), <BotonPc texto="Volver a comprar" icono="ticket" onClick={volverAComprar} />);
            case 'sin-plaza':
                return resultado('undo-2', ['var(--rv-ink)', '#fff'], ['Devuelto', 'confirmed'], 'Te devolvemos el dinero', `Tu pago llegó cuando tus entradas ya se habían liberado y no quedaban más. Te hemos devuelto ${dinero(p.total)} entero: puede tardar unos días en verse en tu cuenta.`, (
                    <>
                        <div className="rv-pc-lineas">
                            <div className="rv-pc-dato"><span>Pedido</span><strong>{`${e.name} · ${p.resumen}`}</strong></div>
                            <div className="rv-pc-dato"><span>Devuelto</span><strong>{dinero(p.total)}</strong></div>
                        </div>
                        {negocio.eventoDelWidget === null && <BotonPc texto="Volver a la cartelera" icono="arrow-right" onClick={() => { setVuelta(null); setElegido(null); }} />}
                        <BotonPc texto="Mis entradas" icono="ticket" tipo="sec" onClick={() => { navegar('/mis-entradas'); }} />
                    </>
                ));
            default:
                return resultado('loader-circle', ['var(--rv-paper)', 'var(--rv-ink)'], ['Pendiente de pago', 'pending'], 'Esperando a tu banco', 'Tu banco todavía no ha confirmado el pago. No vuelvas a pagar: tus entradas siguen guardadas y esta página se actualiza sola en cuanto lo confirme.', (
                    <BotonPc texto="Comprobar ahora" icono="refresh-cw" tipo="sec" ocupado={vuelta.estado === 'comprobando'} ocupadoTexto="Comprobando…" onClick={() => { setVuelta({ pedido: p, estado: 'comprobando' }); }} />
                ), true);
        }
    }

    if (hecha !== null) {
        const tickets = hecha.tickets;
        const n = hecha.n;
        const filas: [string, string][] = [['Localizador', hecha.referencia], ['Cuándo', cuandoLargo(e)], ...(puertas === null ? [] : [['Puertas', puertas] as [string, string]]), ...(hecha.total !== null && hecha.total.amount > 0 ? [['Pagado', dinero(hecha.total)] as [string, string]] : [])];
        return (
            <Marco negocio={negocio} linea={linea}>
                <section className="rv-pc-card" aria-labelledby="rv-ce-hecha">
                    <div className="rv-pc-baldosa-fila">
                        <span aria-hidden="true" className="rv-pc-baldosa" style={{ background: 'var(--rv-red)', color: 'var(--rv-rp-tinta, #fff)' }}><Icono nombre="check" /></span>
                        <Chip estado="confirmed">Confirmado</Chip>
                    </div>
                    <div className="rv-pc-card__cab"><h1 id="rv-ce-hecha">{n === 1 ? 'Ya tienes tu entrada' : 'Ya tienes tus entradas'}</h1><span>{textoHecha(contacto)}</span></div>
                    {tickets.length > 0 && (
                        <div className="rv-ce-pases">
                            <strong className="rv-ce-pases__titulo">{n === 1 ? 'Tu pase' : `Tus ${String(n)} pases`}</strong>
                            <ul>
                                {tickets.map((t, i) => {
                                    const l = lineaDePase(i, n, nombreDeTipo(t.ticket_type_id), t.holder_name, puertas);
                                    const url = t.pass_id == null ? undefined : pases[t.pass_id];
                                    const qr = url === undefined ? null : partesDelQr(url);
                                    const dentro = (
                                        <>
                                            <span aria-hidden="true" className="rv-ce-pase__qr"><Icono nombre="qr-code" /></span>
                                            <span className="rv-ce-pase__texto"><strong>{l.titulo}</strong><small>{l.sub}</small></span>
                                            <Chip estado="confirmed">Válido</Chip>
                                            {url !== undefined && <Icono nombre="chevron-right" />}
                                        </>
                                    );
                                    return (
                                        <li key={t.id} className="rv-ce-pases__item">
                                            {url === undefined ? <div className="rv-ce-pase">{dentro}</div> : <a className="rv-ce-pase" href={url} target={negocio.widget ? '_blank' : '_self'} rel="noopener">{dentro}</a>}
                                            {qr !== null && (
                                                <BotonesCartera
                                                    apple
                                                    google
                                                    ocupada={ocupada}
                                                    onApple={() => { setNotaCartera(null); anadir('apple', qr.negocio, qr.token); }}
                                                    onGoogle={() => { setNotaCartera(null); anadir('google', qr.negocio, qr.token); }}
                                                />
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                            <NotaPase nota={notaCartera} />
                        </div>
                    )}
                    <div className="rv-pc-lineas">
                        {filas.map(([k, v]) => <div key={k} className="rv-pc-dato"><span>{k}</span><strong>{v}</strong></div>)}
                    </div>
                    <BotonPc texto="Mis entradas" icono="ticket" tipo="sec" onClick={() => { navegar('/mis-entradas'); }} />
                    <p className="rv-pc-nota"><Icono nombre="send" /><span>{`¿No te ha llegado ${contacto?.tipo === 'email' ? 'el email' : 'el WhatsApp'}? Vuelve a pedirlo desde «Mis entradas» o desde la página de cada pase. Si el evento cambia de fecha o se cancela, te avisamos.`}</span></p>
                </section>
            </Marco>
        );
    }

    if (paso === 'pagar' && creado !== null && presupuesto !== null) {
        const url = creado.next_action?.url ?? '';
        const hasta = creado.next_action?.expires_at ?? null;
        if (queda === 0) {
            return liberadas();
        }
        const pronto = queda !== null && queda <= CADUCA_PRONTO_SEGUNDOS;
        const metodos: [NombreIcono, string][] = [['credit-card', 'Tarjeta'], ['smartphone', 'Apple Pay'], ['smartphone', 'Google Pay']];
        return (
            <Marco negocio={negocio} linea="Pagar las entradas">
                <section className="rv-pc-card rv-pc-plazo" aria-labelledby="rv-ce-guardadas">
                    <div className="rv-pc-plazo__fila">
                        <span className={`rv-pc-plazo__baldosa${pronto ? ' rv-pc-plazo__baldosa--pronto' : ''}`}><Icono nombre="hourglass" /></span>
                        <span className="rv-pc-plazo__texto">
                            <strong id="rv-ce-guardadas">{cuantas === 1 ? 'Te guardamos tu entrada' : `Te guardamos tus ${String(cuantas)} entradas`}</strong>
                            {hasta !== null && <span>{`Hasta las ${horaEn(hasta, e.timezone)}`}</span>}
                        </span>
                        {queda !== null && (
                            <strong role="timer" className={`rv-pc-plazo__reloj${pronto ? ' rv-pc-plazo__reloj--pronto' : ''}`} aria-label={`Quedan ${String(Math.floor(queda / 60))} minutos y ${String(queda % 60)} segundos`}>
                                {minutosYSegundos(queda)}
                            </strong>
                        )}
                    </div>
                    <span className="rv-pc-plazo__barra" aria-hidden="true"><span style={{ width: `${String(queda === null ? 0 : Math.min(100, (queda / GUARDADAS_SEGUNDOS) * 100))}%` }} className={pronto ? 'rv-pc-plazo__barra--pronto' : undefined} /></span>
                    <div className="rv-pc-plazo__pie">
                        <span>Si no pagas antes, vuelven a quedar libres y no se cobra nada.</span>
                        <Chip estado={pronto ? 'pending' : 'neutral'}>{pronto ? 'Caduca pronto' : 'Guardadas'}</Chip>
                    </div>
                </section>
                <section className="rv-pc-card" aria-labelledby="rv-ce-pagar">
                    <div className="rv-pc-card__cab">
                        <small className="rv-rp-eyebrow">Pago de tus entradas</small>
                        <h1 id="rv-ce-pagar">{e.name}</h1>
                    </div>
                    <ul className="rv-rp-datos">
                        <li><Icono nombre="calendar-days" /><span>{cuandoLargo(e)}</span></li>
                        {presupuesto.lines.map((l) => <li key={l.ticket_type_id}><Icono nombre="ticket" /><span>{`${String(l.quantity)} × ${l.name ?? ''}`}</span></li>)}
                    </ul>
                    <p className="rv-ce-total"><small>Total a pagar</small><strong>{dinero(presupuesto.total)}</strong>{presupuesto.booking_fee.amount > 0 && <small>{`Incluye ${dinero(presupuesto.booking_fee)} de gastos de gestión.`}</small>}</p>
                    <div className="rv-rp-pago"><Icono nombre="scale" /><span><strong>Si el evento cambia</strong><small>{CONDICIONES}</small></span></div>
                </section>
                <BotonPc texto={`Pagar ${dinero(presupuesto.total)}`} icono="lock" onClick={() => { abrir(url); }} />
                <div className="rv-pc-metodos" aria-label="Formas de pago">
                    {metodos.map(([icono, t]) => <span key={t} className="rv-pc-metodo"><Icono nombre={icono} />{t}</span>)}
                </div>
                <p className="rv-pc-nota"><Icono nombre="lock" /><span>El pago se hace en la página segura de Stripe. Nunca vemos ni guardamos los datos de tu tarjeta.</span></p>
            </Marco>
        );
    }

    if (paso === 'identificarse') {
        return (
            <Marco negocio={negocio} linea={linea}>
                {cabeceraEvento}
                <Identificarse
                    ops={ops}
                    contexto={{ tipo: 'negocio', negocio: { id: negocio.id, nombre: negocio.nombre, privacidad: negocio.privacidad, clavePublica: negocio.clave, origen: negocio.widget ? 'widget' : 'web', titulo: { titulo: '¿A nombre de quién?', sub: 'Te mandamos un código para confirmar que eres tú. Con el teléfono, tus entradas te llegan por WhatsApp; con el email, por email. Sin contraseñas: la próxima vez te reconoceremos.' } } }}
                    captcha={captcha}
                    onToken={onToken}
                    onIdentificadoEnNegocio={(r, c) => { setContacto(c); setCliente(r); presupuestar(); }}
                    onNoSoyYo={() => { setCliente(null); setContacto(null); setNombreCliente(null); setPaso('elegir'); }}
                    ahora={ahora}
                />
                {aviso !== null && <AvisoPc texto={aviso.texto} tipo={aviso.tipo} />}
            </Marco>
        );
    }

    if (paso === 'revisar' && presupuesto !== null) {
        const gratis = presupuesto.total.amount === 0;
        return (
            <Marco negocio={negocio} linea={linea}>
                <section className="rv-pc-card" aria-labelledby="rv-ce-revisa">
                    <div className="rv-pc-card__cab"><small className="rv-rp-eyebrow">Revisa tu pedido</small><h1 id="rv-ce-revisa">{e.name}</h1></div>
                    <ul className="rv-rp-datos">
                        <li><Icono nombre="calendar-days" /><span>{cuandoLargo(e)}</span></li>
                        {e.door_opens_at != null && <li><Icono nombre="door-open" /><span>{`Puertas a las ${horaEn(e.door_opens_at, e.timezone)}`}</span></li>}
                        {presupuesto.lines.map((l) => <li key={l.ticket_type_id}><Icono nombre="ticket" /><span>{`${String(l.quantity)} × ${l.name ?? ''}`}</span></li>)}
                        {e.age_notice_min_age != null && <li><Icono nombre="shield" /><span>{`Evento para mayores de ${String(e.age_notice_min_age)} años`}</span></li>}
                    </ul>
                    <button type="button" className="rv-rp-cambiar" onClick={() => { setPaso('elegir'); }}><Icono nombre="pencil" /><span>Cambiar algo</span></button>
                    <div className="rv-pc-lineas">
                        {nombreCliente !== null && <div className="rv-pc-dato"><span>A nombre de</span><strong>{nombreCliente}</strong></div>}
                        {contacto !== null && (
                            <div className="rv-pc-dato rv-ce-envio">
                                <span>Te las mandamos</span>
                                <strong>{`${porDonde(contacto).slice(0, 1).toUpperCase()}${porDonde(contacto).slice(1)}`}</strong>
                                <small>{`Con ${contacto.tipo === 'email' ? 'el email' : 'el teléfono'} con el que te identificaste. Si no te llegan, reenvíalas desde «Mis entradas».`}</small>
                            </div>
                        )}
                    </div>
                    <dl className="rv-ce-importes">
                        {presupuesto.lines.map((l) => <div key={l.ticket_type_id}><dt>{`${String(l.quantity)} × ${l.name ?? ''}`}</dt><dd>{dinero(l.line_total)}</dd></div>)}
                        {presupuesto.booking_fee.amount > 0 && <div><dt>Gastos de gestión</dt><dd>{dinero(presupuesto.booking_fee)}</dd></div>}
                        <div className="rv-ce-importes__total"><dt>Total</dt><dd>{dinero(presupuesto.total)}</dd></div>
                    </dl>
                    <div className="rv-rp-pago"><Icono nombre="scale" /><span><strong>Antes de comprar</strong><small>{CONDICIONES}</small></span></div>
                    {aviso !== null && <AvisoPc texto={aviso.texto} tipo={aviso.tipo} />}
                    <BotonPc texto={gratis ? 'Conseguir mis entradas' : `Comprar y pagar ${dinero(presupuesto.total)}`} icono={gratis ? 'check' : 'lock'} ocupado={ocupado} ocupadoTexto={gratis ? 'Apuntándote…' : 'Guardando tus entradas…'} onClick={() => { void comprar(); }} />
                    {!gratis && <p className="rv-rp-nota"><Icono nombre="hourglass" />{` Al pulsar, te guardamos ${presupuesto.lines.reduce((a, l) => a + l.quantity, 0) === 1 ? 'la entrada' : 'las entradas'} 30 minutos mientras pagas.`}</p>}
                    <LegalCaptcha />
                </section>
            </Marco>
        );
    }

    // ---- Ficha del evento (tarea 64) ----
    if (paso === 'ficha') {
        const estado = estadoVenta(e);
        const chip = chipDeLaFicha(e);
        const avisoFicha = avisoDeLaFicha(e);
        const cambio = textoCambioDeFecha(e);
        const gratisFicha = e.ticket_types.some(sePuedeComprar) && e.ticket_types.filter(sePuedeComprar).every((t) => t.price.amount === 0);
        const hoyEs = e.local_start_date === hoy;
        const verCartelera = negocio.eventoDelWidget === null ? () => { setElegido(null); } : null;
        return (
            <Marco negocio={negocio} linea={linea}>
                {verCartelera !== null && <button type="button" className="rv-rp-cambiar" onClick={verCartelera}><Icono nombre="arrow-left" /><span>Cartelera</span></button>}
                <section className="rv-pc-card rv-ce-ficha" aria-labelledby="rv-ce-ficha">
                    <div className="rv-ce-ficha__cab">
                        <span className="rv-ce-evento__cartel rv-ce-ficha__cartel" style={e.image_url == null ? undefined : { backgroundImage: `url(${e.image_url})` }}>{e.image_url == null && <strong>{e.name}</strong>}</span>
                        <div className="rv-ce-ficha__titulo">
                            <span className={`rv-rp-eyebrow${hoyEs ? ' rv-ce-ficha__hoy' : ''}`}>{hoyEs ? `Hoy · ${e.local_start_time.slice(0, 5)}` : cuandoLargo(e).split(' · ')[0]}</span>
                            {chip !== null && <span><Chip estado={chip.estado}>{chip.texto}</Chip></span>}
                            <h1 id="rv-ce-ficha">{e.name}</h1>
                        </div>
                    </div>
                    <ul className="rv-rp-datos">
                        {lineasDeLaFicha(e).map((l) => <li key={l.icono}><Icono nombre={l.icono} /><span>{l.texto}</span></li>)}
                    </ul>
                    {cambio !== null && <div className="rv-rp-pago" role="status"><Icono nombre="calendar-clock" /><span><strong>La fecha ha cambiado</strong><small>{cambio}</small></span></div>}
                    {e.description !== undefined && e.description !== '' && <p className="rv-ce-ficha__desc">{e.description}</p>}
                </section>
                {avisoFicha !== null && (
                    <section className="rv-pc-card" role="status">
                        <div className="rv-pc-baldosa-fila"><Baldosa icono={avisoFicha.icono} fondo="var(--rv-paper)" color="var(--rv-ink)" /><span className="rv-pc-card__cab"><h2>{avisoFicha.titulo}</h2><span>{avisoFicha.texto}</span></span></div>
                        {verCartelera !== null && <BotonPc texto="Ver la cartelera" icono="calendar" tipo="sec" onClick={verCartelera} />}
                    </section>
                )}
                {estado !== 'solo-listas' && e.ticket_types.length > 0 && (
                    <section className="rv-pc-card" aria-labelledby="rv-ce-ficha-tipos">
                        <div className="rv-pc-card__cab"><h2 id="rv-ce-ficha-tipos">Entradas</h2><span>{gratisFicha ? 'Gratis, pero hay que apuntarse: el aforo es limitado.' : 'Precio por entrada. Al pagar se suman los gastos de gestión.'}</span></div>
                        <div className="rv-ce-tipos">
                            {e.ticket_types.map((t) => {
                                const fuera = !t.on_sale_now ? (estado === 'venta-no-abierta' ? 'Próximamente' : 'Fuera de venta') : t.available === 0 ? 'Agotado' : null;
                                return (
                                    <div key={t.id} className={`rv-ce-tipo rv-ce-tipo--ficha${fuera !== null && estado === 'a-la-venta' ? ' rv-ce-tipo--fuera' : ''}`}>
                                        <span><strong>{t.name}</strong>{t.description !== undefined && t.description !== '' && <small>{t.description}</small>}</span>
                                        <span className="rv-ce-tipo__precio">
                                            <strong>{t.price.amount === 0 ? 'Gratis' : dinero(t.price)}</strong>
                                            {fuera !== null && <Chip estado={fuera === 'Agotado' ? 'problem' : 'neutral'}>{fuera}</Chip>}
                                            {fuera === null && t.available != null && t.available <= 10 && <small>{t.available === 1 ? 'Queda 1' : `Quedan ${String(t.available)}`}</small>}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}
                {estado === 'a-la-venta' && <BotonPc texto={gratisFicha ? 'Conseguir entradas' : 'Comprar entradas'} icono="ticket" onClick={() => { setAviso(null); setPaso('elegir'); }} />}
                <button type="button" className="rv-rp-cambiar" onClick={() => { navegar('/mis-entradas'); }}><Icono nombre="ticket" /><span>¿Ya tienes entradas? Mis entradas</span></button>
            </Marco>
        );
    }

    // ---- Elegir ----
    const sinVenta = textoSinVenta(e);
    return (
        <Marco negocio={negocio} linea={linea}>
            {cabeceraEvento}
            <section className="rv-pc-card" aria-labelledby="rv-ce-elige">
                <div className="rv-pc-card__cab"><h1 id="rv-ce-elige">Elige tus entradas</h1><span>Elige cuántas quieres de cada tipo. No se guardan hasta que pagues.</span></div>
                {e.rescheduled_from != null && <p className="rv-rp-nota"><Icono nombre="calendar-clock" />{' Ha cambiado de fecha. Si ya tienes entradas, siguen valiendo para la fecha nueva.'}</p>}
                {aviso !== null && <AvisoPc texto={aviso.texto} tipo={aviso.tipo} />}
                {sinVenta !== null ? (
                    <p className="rv-rp-nota">{sinVenta}</p>
                ) : (
                    <>
                        <div className="rv-ce-tipos">
                            {e.ticket_types.map((t) => {
                                const n = carrito[t.id] ?? 0;
                                return sePuedeComprar(t) ? (
                                    <div key={t.id} className="rv-ce-tipo">
                                        <Contador etiqueta={t.name} ayuda={[t.description ?? '', dinero(t.price)].filter((x) => x !== '').join(' · ')} valor={n} min={0} max={Math.max(n, maximoDe(t, carrito, maxPorPedido))} onCambio={(v) => { setCarrito({ ...carrito, [t.id]: v }); }} />
                                        {nominativo && Array.from({ length: n }, (_, i) => (
                                            <Campo key={i} etiqueta={`Nombre de quien va (${t.name} ${String(i + 1)})`} valor={nombres[t.id]?.[i] ?? ''} max={120} onChange={(v) => { const lista = [...(nombres[t.id] ?? [])]; lista[i] = v; setNombres({ ...nombres, [t.id]: lista }); }} />
                                        ))}
                                    </div>
                                ) : (
                                    <div key={t.id} className="rv-ce-tipo rv-ce-tipo--fuera">
                                        <span><strong>{t.name}</strong>{t.description !== undefined && <small>{t.description}</small>}<small>{dinero(t.price)}</small></span>
                                        <span className="rv-ce-chip">{t.available === 0 ? 'Agotado' : 'Fuera de venta'}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <p className="rv-rp-nota"><Icono nombre="info" />{` Hasta ${String(maxPorPedido)} entradas por pedido.`}</p>
                        {nominativo && <p className="rv-rp-nota">Cada entrada lleva el nombre de quien va.</p>}
                        <div className="rv-rp-pago"><Icono nombre={contacto === null ? 'send' : contacto.tipo === 'email' ? 'mail' : 'message-circle'} /><span><strong>{textoEnvio(contacto).titulo}</strong><small>{textoEnvio(contacto).sub}</small></span></div>
                        <BotonPc texto="Seguir" icono="arrow-right" desactivado={cuantas === 0 || faltanNombres(carrito, nominativo, nombres)} ocupado={ocupado} onClick={seguir} />
                    </>
                )}
                {negocio.eventoDelWidget === null && <button type="button" className="rv-rp-cambiar" onClick={() => { setElegido(null); }}><Icono nombre="arrow-left" /><span>Ver la cartelera</span></button>}
            </section>
        </Marco>
    );
}
